import { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import MembershipStepper from "./MembershipStepper";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import { MESSAGE_TYPES } from "../../../utils/editIframe";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import {
  clearValidationErrors,
  executeValidationWithArrays,
  handleResetWithValidation,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Membership", path: `/membership` },
];

export default function MembershipEditPage() {
  const contentKey = "membershipContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const DEFAULT_COUPON = {
    title: "",
    method: "",
    discount: 0,
    description: "",
    note: "",
    yes_label: "",
    yes_note: "",
    no_label: "",
  };
  const DEFAULT_BANK = {
    title: "",
    description: "",
    option1: { title: "", description: "", note: "" },
    option2: { title: "", description: "", note: "" },
  };
  const [pageContent, setPageContent] = useState({
    grades: [],
    courses: [],
    fees: [],
    coupon: DEFAULT_COUPON,
    bank: DEFAULT_BANK,
  });

  const postMessageToIframe = useCallback(
    (newData) => {
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: MESSAGE_TYPES.TEXT_UPDATE, contentKey, allData: newData },
          process.env.REACT_APP_ORIGIN,
        );
      }
    },
    [contentKey],
  );

  // グループのDnD
  const handleGroupReorder = useCallback(
    (reorderedGroups) => {
      setPageContent((prev) => {
        clearValidationErrors();
        const newData = { ...prev, grades: reorderedGroups };
        postMessageToIframe(newData);
        return newData;
      });
    },
    [postMessageToIframe],
  );

  const { getDraggableProps } = useDragAndDrop(
    pageContent.grades || [],
    handleGroupReorder,
  );

  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_page_membership_content/`,
      );
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      if (!data.grades?.length) data.grades = [{ label: "", items: [""] }];
      if (!data.courses?.length)
        data.courses = [{ name: "", fee: 0, times: null, is_young: false, is_unlimited: false }];
      data.courses = data.courses.map((c) => ({
        times: null,
        is_young: false,
        is_unlimited: false,
        ...c,
      }));
      if (!data.fees?.length) data.fees = [{ name: "", amount: 0 }];
      if (!data.coupon || !Object.keys(data.coupon).length)
        data.coupon = DEFAULT_COUPON;
      if (!data.bank || !Object.keys(data.bank).length)
        data.bank = DEFAULT_BANK;
      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
  }, [fetchPageContentData]);

  // --- 学年ハンドラ ---
  const handleGradeFocus = () => {
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.SCROLL_TO,
          target: ".edit_membership_grades",
          contentKey,
        },
        process.env.REACT_APP_ORIGIN,
      );
    }
  };

  const handleGroupLabelChange = (gIdx, value) => {
    setPageContent((prev) => {
      const newGrades = prev.grades.map((g, i) =>
        i === gIdx ? { ...g, label: value } : g,
      );
      const newData = { ...prev, grades: newGrades };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const handleItemChange = (gIdx, iIdx, value) => {
    setPageContent((prev) => {
      const newGrades = prev.grades.map((g, i) =>
        i === gIdx
          ? {
              ...g,
              items: g.items.map((item, j) => (j === iIdx ? value : item)),
            }
          : g,
      );
      const newData = { ...prev, grades: newGrades };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const addGroup = (insertAfterIndex = null) => {
    setPageContent((prev) => {
      const newGroup = { label: "", items: [""] };
      const newGrades =
        insertAfterIndex !== null
          ? [
              ...prev.grades.slice(0, insertAfterIndex + 1),
              newGroup,
              ...prev.grades.slice(insertAfterIndex + 1),
            ]
          : [...prev.grades, newGroup];
      const newData = { ...prev, grades: newGrades };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const removeGroup = (gIdx) => {
    if (pageContent.grades.length <= 1) return;
    setPageContent((prev) => {
      const newData = {
        ...prev,
        grades: prev.grades.filter((_, i) => i !== gIdx),
      };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const addItem = (gIdx) => {
    setPageContent((prev) => {
      const newGrades = prev.grades.map((g, i) =>
        i === gIdx ? { ...g, items: [...g.items, ""] } : g,
      );
      const newData = { ...prev, grades: newGrades };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const removeItem = (gIdx, iIdx) => {
    if (pageContent.grades[gIdx].items.length <= 1) return;
    setPageContent((prev) => {
      const newGrades = prev.grades.map((g, i) =>
        i === gIdx ? { ...g, items: g.items.filter((_, j) => j !== iIdx) } : g,
      );
      const newData = { ...prev, grades: newGrades };
      postMessageToIframe(newData);
      return newData;
    });
  };

  // --- コースハンドラ ---
  const handleCourseCheckChange = (index, fieldName, checked) => {
    setPageContent((prev) => {
      const newCourses = prev.courses.map((item, i) =>
        i === index ? { ...item, [fieldName]: checked } : item,
      );
      const newData = { ...prev, courses: newCourses };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const handleCourseInputChange = (index, fieldName, rawValue) => {
    const value =
      fieldName === "fee"
        ? rawValue.replace(/[^0-9]/g, "") === ""
          ? 0
          : Number(rawValue.replace(/[^0-9]/g, ""))
        : fieldName === "times"
          ? rawValue.replace(/[^0-9]/g, "") === ""
            ? null
            : Number(rawValue.replace(/[^0-9]/g, ""))
          : rawValue;
    setPageContent((prev) => {
      const newCourses = prev.courses.map((item, i) =>
        i === index ? { ...item, [fieldName]: value } : item,
      );
      const newData = { ...prev, courses: newCourses };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const handleCourseFocus = () => {
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.SCROLL_TO,
          target: ".edit_membership_courses",
          contentKey,
        },
        process.env.REACT_APP_ORIGIN,
      );
    }
  };

  const addCourse = () => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        courses: [
          ...prev.courses,
          { name: "", fee: 0, times: null, is_young: false, is_unlimited: false },
        ],
      };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const removeCourse = (index) => {
    if (pageContent.courses.length <= 1) return;
    setPageContent((prev) => {
      const newData = {
        ...prev,
        courses: prev.courses.filter((_, i) => i !== index),
      };
      postMessageToIframe(newData);
      return newData;
    });
  };

  // --- 料金明細ハンドラ ---
  const handleFeeInputChange = (index, fieldName, rawValue) => {
    const value =
      fieldName === "amount"
        ? rawValue.replace(/[^0-9]/g, "") === ""
          ? 0
          : Number(rawValue.replace(/[^0-9]/g, ""))
        : rawValue;
    setPageContent((prev) => {
      const newFees = prev.fees.map((item, i) =>
        i === index ? { ...item, [fieldName]: value } : item,
      );
      const newData = { ...prev, fees: newFees };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const handleFeeFocus = () => {
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.SCROLL_TO,
          target: ".edit_membership_fees",
          contentKey,
        },
        process.env.REACT_APP_ORIGIN,
      );
    }
  };

  const addFee = () => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        fees: [...prev.fees, { name: "", amount: 0 }],
      };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const removeFee = (index) => {
    if (pageContent.fees.length <= 1) return;
    setPageContent((prev) => {
      const newData = {
        ...prev,
        fees: prev.fees.filter((_, i) => i !== index),
      };
      postMessageToIframe(newData);
      return newData;
    });
  };

  // --- クーポンハンドラ ---
  const handleCouponChange = (fieldName, rawValue) => {
    const value =
      fieldName === "discount"
        ? rawValue.replace(/[^0-9]/g, "") === ""
          ? 0
          : Number(rawValue.replace(/[^0-9]/g, ""))
        : rawValue;
    setPageContent((prev) => {
      const newData = {
        ...prev,
        coupon: { ...prev.coupon, [fieldName]: value },
      };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const handleCouponFocus = () => {
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.SCROLL_TO,
          target: ".edit_membership_coupon",
          contentKey,
        },
        process.env.REACT_APP_ORIGIN,
      );
    }
  };

  // --- 口座登録ハンドラ ---
  const handleBankChange = (path, rawValue) => {
    setPageContent((prev) => {
      const newBank = { ...prev.bank };
      if (path.includes(".")) {
        const [optKey, field] = path.split(".");
        newBank[optKey] = { ...newBank[optKey], [field]: rawValue };
      } else {
        newBank[path] = rawValue;
      }
      const newData = { ...prev, bank: newBank };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const handleBankFocus = () => {
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.SCROLL_TO,
          target: ".edit_membership_bank",
          contentKey,
        },
        process.env.REACT_APP_ORIGIN,
      );
    }
  };

  const arrayValidationRules = {
    courses: {
      name: { required: true },
      fee: { required: true },
      times: { required: false },
      is_young: { required: false },
      is_unlimited: { required: false },
    },
    fees: {
      name: { required: true },
    },
  };

  const handlePageDataSubmit = async () => {
    if (
      !executeValidationWithArrays(
        pageContent,
        {},
        arrayValidationRules,
        {},
        setMessage,
      )
    ) {
      return;
    }
    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_page_membership_content/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ page_content: pageContent }),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setMessage(data.message);
      await fetchPageContentData();
      setTimeout(() => {
        const editArea = document.querySelector(".editarea");
        if (editArea) editArea.scrollTop = 0;
      }, 100);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      setMessage("更新に失敗しました");
      setTimeout(() => setMessage(""), 3000);
    }
    setLoadingText("");
  };

  const handleReset = () => {
    handleResetWithValidation(fetchPageContentData);
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />
            <MembershipStepper active="form" />

            <div className="form">
              {/* 学年セクション */}
              <section>
                <h4 className="section-title">学年</h4>
                <div className="area4">
                  <div className="item">
                    <div className="nest-item-wrap">
                      {pageContent.grades?.map((group, gIdx) => (
                        <div
                          className="nest-item"
                          key={gIdx}
                          {...getDraggableProps(gIdx)}
                        >
                          <div className="left ctrl">
                            <p>{gIdx + 1}</p>
                            <div className="icon-wrap">
                              <i
                                className="icon delete"
                                style={{ cursor: "pointer" }}
                                onClick={() => removeGroup(gIdx)}
                              ></i>
                              <i
                                className="icon add"
                                style={{ cursor: "pointer" }}
                                onClick={() => addGroup(gIdx)}
                              ></i>
                            </div>
                          </div>
                          <div className="right">
                            <input
                              type="text"
                              value={group.label ?? ""}
                              onChange={(e) =>
                                handleGroupLabelChange(gIdx, e.target.value)
                              }
                              onFocus={handleGradeFocus}
                              placeholder="カテゴリ名（例：幼稚園・保育園）"
                            />

                            <dl className="information txt-form genres">
                              {group.items?.map((item, iIdx) => (
                                <div key={iIdx} style={{ display: "contents" }}>
                                  <dd
                                    onClick={(e) => {
                                      if (e.target.tagName !== "INPUT")
                                        removeItem(gIdx, iIdx);
                                    }}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <input
                                      type="text"
                                      className="q"
                                      value={item ?? ""}
                                      onChange={(e) =>
                                        handleItemChange(
                                          gIdx,
                                          iIdx,
                                          e.target.value,
                                        )
                                      }
                                      onFocus={handleGradeFocus}
                                      placeholder="学年（例：年少）"
                                    />
                                  </dd>
                                </div>
                              ))}
                            </dl>

                            <div className="accent-wrap">
                              <p
                                onClick={() => addItem(gIdx)}
                                style={{ cursor: "pointer" }}
                              >
                                <i className="icon add"></i>
                                <span>Grade</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="accent-wrap">
                  <p onClick={() => addGroup()} style={{ cursor: "pointer" }}>
                    <i className="icon add"></i>
                    <span>Category</span>
                  </p>
                </div>
              </section>

              {/* コースセクション */}
              <section>
                <h4 className="section-title">コース</h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  {pageContent.courses?.map((course, index) => (
                    <div key={index}>
                      {/* 1行目: コース名・回数・料金・削除 */}
                      <div className="course-row-inputs">
                        <input
                          type="text"
                          name={`course${index}_name`}
                          value={course.name ?? ""}
                          onChange={(e) =>
                            handleCourseInputChange(
                              index,
                              "name",
                              e.target.value,
                            )
                          }
                          onFocus={handleCourseFocus}
                          placeholder="コース名"
                          data-required="true"
                          style={{ flex: 3 }}
                        />
                        <input
                          type="text"
                          inputMode="numeric"
                          name={`course${index}_times`}
                          value={course.times ?? ""}
                          onChange={(e) =>
                            handleCourseInputChange(
                              index,
                              "times",
                              e.target.value,
                            )
                          }
                          onFocus={handleCourseFocus}
                          placeholder="回数"
                          style={{ flex: 1 }}
                        />
                        <input
                          type="text"
                          inputMode="numeric"
                          name={`course${index}_fee`}
                          value={course.fee ?? ""}
                          onChange={(e) =>
                            handleCourseInputChange(
                              index,
                              "fee",
                              e.target.value,
                            )
                          }
                          onFocus={handleCourseFocus}
                          placeholder="料金"
                          data-required="true"
                          style={{ flex: 2 }}
                        />
                        <span
                          className="course-remove-btn"
                          onClick={() => removeCourse(index)}
                        >
                          ×
                        </span>
                      </div>
                      {/* 2行目: 幼少クラス / 受け放題コース */}
                      <div className="course-flag-input">
                        <label>
                          <input
                            type="checkbox"
                            name={`course${index}_is_young`}
                            checked={course.is_young ?? false}
                            onChange={(e) =>
                              handleCourseCheckChange(
                                index,
                                "is_young",
                                e.target.checked,
                              )
                            }
                          />
                          幼少クラス
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            name={`course${index}_is_unlimited`}
                            checked={course.is_unlimited ?? false}
                            onChange={(e) =>
                              handleCourseCheckChange(
                                index,
                                "is_unlimited",
                                e.target.checked,
                              )
                            }
                          />
                          受け放題コース
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="accent-wrap">
                  <p onClick={addCourse} style={{ cursor: "pointer" }}>
                    <i className="icon add"></i>
                    <span>Course</span>
                  </p>
                </div>
              </section>

              {/* 料金明細セクション */}
              <section>
                <h4 className="section-title">料金明細</h4>
                <dl className="information txt-form">
                  {pageContent.fees?.map((fee, index) => (
                    <div key={index} style={{ display: "contents" }}>
                      <dt>
                        <input
                          type="text"
                          name={`fee${index}_name`}
                          className="q"
                          value={fee.name ?? ""}
                          onChange={(e) =>
                            handleFeeInputChange(index, "name", e.target.value)
                          }
                          onFocus={handleFeeFocus}
                          placeholder="項目名（例：入会金）"
                          data-required="true"
                        />
                      </dt>
                      <dd
                        onClick={(e) => {
                          if (e.target.tagName !== "INPUT") removeFee(index);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="text"
                          inputMode="numeric"
                          name={`fee${index}_amount`}
                          className="q"
                          value={fee.amount ?? ""}
                          onChange={(e) =>
                            handleFeeInputChange(
                              index,
                              "amount",
                              e.target.value,
                            )
                          }
                          onFocus={handleFeeFocus}
                          placeholder="金額（例：5500）"
                        />
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="accent-wrap">
                  <p onClick={addFee} style={{ cursor: "pointer" }}>
                    <i className="icon add"></i>
                    <span>Price</span>
                  </p>
                </div>
              </section>

              {/* クーポンセクション */}
              <section>
                <h4 className="section-title">クーポン</h4>
                <div className="area3">
                  <div className="item">
                    {/* 見出し: 1カラム */}
                    <input
                      type="text"
                      value={pageContent.coupon?.title ?? ""}
                      onChange={(e) =>
                        handleCouponChange("title", e.target.value)
                      }
                      onFocus={handleCouponFocus}
                      placeholder="見出し（例：クーポンの利用）"
                    />
                    {/* 方法名 + 割引額: 2カラム */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.5rem",
                      }}
                    >
                      <input
                        type="text"
                        value={pageContent.coupon?.method ?? ""}
                        onChange={(e) =>
                          handleCouponChange("method", e.target.value)
                        }
                        onFocus={handleCouponFocus}
                        placeholder="方法名（例：口コミを記入）"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={pageContent.coupon?.discount ?? ""}
                        onChange={(e) =>
                          handleCouponChange("discount", e.target.value)
                        }
                        onFocus={handleCouponFocus}
                        placeholder="割引額（例：3300）"
                      />
                    </div>
                    {/* 説明文: 1カラム */}
                    <input
                      type="text"
                      value={pageContent.coupon?.description ?? ""}
                      onChange={(e) =>
                        handleCouponChange("description", e.target.value)
                      }
                      onFocus={handleCouponFocus}
                      placeholder="説明文（例：クーポンをご利用の方は...）"
                    />
                    {/* 注意書き: 1カラム */}
                    <input
                      type="text"
                      value={pageContent.coupon?.note ?? ""}
                      onChange={(e) =>
                        handleCouponChange("note", e.target.value)
                      }
                      onFocus={handleCouponFocus}
                      placeholder="注意書き（例：※クーポン適応価格は...）"
                    />
                    {/* 適用ラベル + 非適用ラベル: 2カラム */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.5rem",
                      }}
                    >
                      <input
                        type="text"
                        value={pageContent.coupon?.yes_label ?? ""}
                        onChange={(e) =>
                          handleCouponChange("yes_label", e.target.value)
                        }
                        onFocus={handleCouponFocus}
                        placeholder="適用ラベル（例：記入する）"
                      />
                      <input
                        type="text"
                        value={pageContent.coupon?.no_label ?? ""}
                        onChange={(e) =>
                          handleCouponChange("no_label", e.target.value)
                        }
                        onFocus={handleCouponFocus}
                        placeholder="非適用ラベル（例：記入しない）"
                      />
                    </div>
                    {/* 適用時補足: 1カラム */}
                    <input
                      type="text"
                      value={pageContent.coupon?.yes_note ?? ""}
                      onChange={(e) =>
                        handleCouponChange("yes_note", e.target.value)
                      }
                      onFocus={handleCouponFocus}
                      placeholder="適用時補足（例：店舗受付にてご記入いただきます）"
                    />
                  </div>
                </div>
              </section>

              {/* 口座登録セクション */}
              <section>
                <h4 className="section-title">口座登録</h4>
                <div className="area3">
                  <div className="item">
                    {/* 見出し: 1カラム */}
                    <input
                      type="text"
                      value={pageContent.bank?.title ?? ""}
                      onChange={(e) =>
                        handleBankChange("title", e.target.value)
                      }
                      onFocus={handleBankFocus}
                      placeholder="見出し（例：口座の登録方法をお選びください）"
                    />
                    {/* 説明: 1カラム */}
                    <input
                      type="text"
                      value={pageContent.bank?.description ?? ""}
                      onChange={(e) =>
                        handleBankChange("description", e.target.value)
                      }
                      onFocus={handleBankFocus}
                      placeholder="説明（例：下記の方法から...）"
                    />
                    {/* 選択肢1: 見出し 1カラム、説明 + 注意書き 2カラム */}
                    <input
                      type="text"
                      value={pageContent.bank?.option1?.title ?? ""}
                      onChange={(e) =>
                        handleBankChange("option1.title", e.target.value)
                      }
                      onFocus={handleBankFocus}
                      placeholder="選択肢1 見出し（例：ネットで簡単登録）"
                    />
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.5rem",
                      }}
                    >
                      <input
                        type="text"
                        value={pageContent.bank?.option1?.description ?? ""}
                        onChange={(e) =>
                          handleBankChange(
                            "option1.description",
                            e.target.value,
                          )
                        }
                        onFocus={handleBankFocus}
                        placeholder="選択肢1 説明（例：対応銀行) 肥後銀行...）"
                      />
                      <input
                        type="text"
                        value={pageContent.bank?.option1?.note ?? ""}
                        onChange={(e) =>
                          handleBankChange("option1.note", e.target.value)
                        }
                        onFocus={handleBankFocus}
                        placeholder="選択肢1 注意書き（例：※次ページにて必ずご登録...）"
                      />
                    </div>
                    {/* 選択肢2: 見出し 1カラム、説明 + 注意書き 2カラム */}
                    <input
                      type="text"
                      value={pageContent.bank?.option2?.title ?? ""}
                      onChange={(e) =>
                        handleBankChange("option2.title", e.target.value)
                      }
                      onFocus={handleBankFocus}
                      placeholder="選択肢2 見出し（例：その他銀行登録）"
                    />
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.5rem",
                      }}
                    >
                      <input
                        type="text"
                        value={pageContent.bank?.option2?.description ?? ""}
                        onChange={(e) =>
                          handleBankChange(
                            "option2.description",
                            e.target.value,
                          )
                        }
                        onFocus={handleBankFocus}
                        placeholder="選択肢2 説明（例：上記4つ以外の銀行にて...）"
                      />
                      <input
                        type="text"
                        value={pageContent.bank?.option2?.note ?? ""}
                        onChange={(e) =>
                          handleBankChange("option2.note", e.target.value)
                        }
                        onFocus={handleBankFocus}
                        placeholder="選択肢2 注意書き（任意）"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe path="/membership" data={pageContent} contentKey={contentKey} />
    </>
  );
}
